import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  HomeIcon, UsersIcon, CalendarIcon, ClipboardDocumentListIcon,
  ChartBarIcon, FlagIcon, TrophyIcon, ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon, PresentationChartLineIcon,
  BanknotesIcon, Cog6ToothIcon, ArrowRightStartOnRectangleIcon,
  Bars3Icon, XMarkIcon, UserGroupIcon, AcademicCapIcon,
  BoltIcon, BellAlertIcon, HeartIcon, BuildingOffice2Icon,
  ChatBubbleLeftEllipsisIcon, BriefcaseIcon,
  CalendarDaysIcon, DocumentTextIcon, ArrowRightOnRectangleIcon,
  SparklesIcon, ChartPieIcon, CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import clsx from 'clsx';

// Role = hr_admin | manager | employee | super_admin
const ALL_ITEMS = [
  { label: 'Dashboard',     to: '/app',              icon: HomeIcon,                         roles: ['*'] },
  { label: 'Employees',     to: '/app/employees',    icon: UsersIcon,                        roles: ['hr_admin','manager','super_admin'] },
  { label: 'Cycles',        to: '/app/cycles',       icon: CalendarIcon,                     roles: ['hr_admin','manager','super_admin'] },
  { label: 'Reviews',       to: '/app/reviews',      icon: ClipboardDocumentListIcon,        roles: ['*'] },
  { label: 'Review Templates', to: '/app/reviews/templates', icon: ClipboardDocumentListIcon, sub: true, roles: ['hr_admin','super_admin'] },
  { label: 'Calibration',   to: '/app/cycles',       icon: ChartBarIcon, sub: true,          roles: ['hr_admin','manager','super_admin'] },
  { label: 'PIPs',          to: '/app/pips',         icon: FlagIcon,                         roles: ['hr_admin','manager','super_admin'] },
  { label: 'Goals / OKRs',  to: '/app/goals',        icon: TrophyIcon,                       roles: ['*'] },
  { label: '1:1 Meetings',  to: '/app/meetings',     icon: ChatBubbleLeftEllipsisIcon,       roles: ['*'] },
  { label: 'Feedback',      to: '/app/feedback',     icon: ChatBubbleLeftRightIcon,          roles: ['*'] },
  { label: 'Surveys',       to: '/app/surveys',      icon: ClipboardDocumentCheckIcon,       roles: ['*'] },
  { label: 'Analytics',     to: '/app/analytics',    icon: PresentationChartLineIcon,        roles: ['hr_admin','manager','super_admin'] },
  { label: 'AI Insights',   to: '/app/ai-insights',  icon: SparklesIcon,                     roles: ['hr_admin','manager','super_admin'] },
  { label: 'Compensation',  to: '/app/compensation', icon: BanknotesIcon,                    roles: ['hr_admin','super_admin'] },
  { label: 'Benchmarks',    to: '/app/benchmarks',   icon: ChartPieIcon,                     roles: ['hr_admin','super_admin'] },
  { label: 'Workforce',     to: '/app/workforce',    icon: UsersIcon,                        roles: ['hr_admin','super_admin'] },
  { label: 'Payroll',       to: '/app/payroll',      icon: CurrencyDollarIcon,               roles: ['hr_admin','super_admin'] },
  { label: 'Succession',    to: '/app/succession',   icon: UserGroupIcon,                    roles: ['hr_admin','super_admin'] },
  { label: 'Job Board',     to: '/app/job-board',    icon: BriefcaseIcon,                    roles: ['*'] },
  { label: 'HRIS',          to: '/app/hris',         icon: BuildingOffice2Icon,              roles: ['hr_admin','super_admin'] },
  { label: 'Time Off',      to: '/app/hris?tab=time-off',    icon: CalendarDaysIcon,           sub: true, roles: ['*'] },
  { label: 'Documents',     to: '/app/hris?tab=documents',   icon: DocumentTextIcon,           sub: true, roles: ['hr_admin','manager','super_admin'] },
  { label: 'Onboarding',    to: '/app/hris?tab=onboarding',  icon: AcademicCapIcon,            sub: true, roles: ['hr_admin','manager','super_admin'] },
  { label: 'Offboarding',   to: '/app/hris?tab=offboarding', icon: ArrowRightOnRectangleIcon,  sub: true, roles: ['hr_admin','manager','super_admin'] },
  { label: 'Learning',      to: '/app/learning',     icon: AcademicCapIcon,                  roles: ['*'] },
  { label: 'My Learning',   to: '/app/learning/mine',      icon: AcademicCapIcon, sub: true, roles: ['*'] },
  { label: 'Catalog',       to: '/app/learning/catalog',   icon: AcademicCapIcon, sub: true, roles: ['*'] },
  { label: 'Paths',         to: '/app/learning/paths',     icon: AcademicCapIcon, sub: true, roles: ['*'] },
  { label: 'My IDP',        to: '/app/learning/idp',       icon: AcademicCapIcon, sub: true, roles: ['*'] },
  { label: 'Mandatory (HR)',to: '/app/learning/mandatory', icon: AcademicCapIcon, sub: true, roles: ['hr_admin','super_admin'] },
  { label: 'Integrations',  to: '/app/integrations', icon: BoltIcon,                         roles: ['hr_admin','super_admin'] },
  { label: 'Webhooks',      to: '/app/webhooks',     icon: BellAlertIcon,                    roles: ['hr_admin','super_admin'] },
  { label: 'Data Health',   to: '/app/data-health',  icon: HeartIcon,                        roles: ['hr_admin','super_admin'] },
  { label: 'My Payroll',    to: '/app/my-payroll',   icon: BanknotesIcon,                    roles: ['*'] },
  { label: 'Settings',      to: '/app/settings',     icon: Cog6ToothIcon,                    roles: ['hr_admin','super_admin'] },
];

function itemsForRole(role) {
  return ALL_ITEMS.filter(i => i.roles.includes('*') || i.roles.includes(role));
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [open, setOpen]  = useState(false);
  const nav              = itemsForRole(user?.role || 'employee');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const content = (
    <div className="flex flex-col h-full bg-gray-900 text-white w-64">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-gray-700 flex items-center gap-3">
        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center font-bold text-sm">C</div>
        <span className="font-bold text-lg tracking-tight">CalibZa</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {nav.map(({ label, to, icon: Icon, sub }) => (
          <NavLink
            key={label}
            to={to}
            end={to === '/app'}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                sub && 'ml-4 text-xs',
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              )
            }
          >
            <Icon className="w-5 h-5 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t border-gray-700">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-xs font-semibold">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-gray-400 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <aside className="hidden lg:flex flex-col h-screen sticky top-0">{content}</aside>

      {/* Mobile toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-gray-900 text-white rounded-lg"
        onClick={() => setOpen(o => !o)}
      >
        {open ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
      </button>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0">{content}</div>
        </div>
      )}
    </>
  );
}
